import React, { useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Bell, Users, LogOut, ChevronDown, Settings } from 'lucide-react';
import { useScale, scaleW, scaleH } from '../../hooks/useScale';
import resolveAvatar from '../../lib/resolveAvatar';

import { fetchWithAuth } from '../../lib/fetchWithAuth';
const TurtleWithProfile = ({ size = 100, profilePicture, onProfileClick }: { size?: number; profilePicture?: string; onProfileClick?: () => void }) => {
  // Profile picture bigger to fill cutout - using percentage for proper scaling
  const profileSizePercent = 42; // 42% of turtle size
  // Use proportional positioning based on percentage - moved up slightly (was 22%)
  const turtleOffsetPercent = 21; // 21% of turtle size

  return (
    <div style={{ position: 'relative', width: '100%', height: '100%' }}>
      {/* Turtle SVG from backend - lower position */}
      <img
        src="/api/assets/profile-logo"
        alt=""
        style={{
          width: '100%',
          height: '100%',
          position: 'absolute',
          top: `${turtleOffsetPercent}%`,
          left: 0,
          zIndex: 1,
          pointerEvents: 'none'
        }}
      />

      {/* Profile Picture in the circular cutout - clickable */}
      <Link
        to="/u"
        style={{
          position: 'absolute',
          width: `${profileSizePercent}%`,
          height: `${profileSizePercent}%`,
          borderRadius: '50%',
          overflow: 'hidden',
          top: `calc(50% + ${turtleOffsetPercent}%)`,
          left: '50%',
          transform: 'translate(-50%, -50%)',
          zIndex: 0,
          display: 'block',
          textDecoration: 'none'
        }}
        className="hover:opacity-80 transition-opacity"
      >
        {profilePicture ? (
          <img
            src={resolveAvatar(profilePicture) || profilePicture}
            alt="Profile"
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover'
            }}
          />
        ) : (
          <div style={{
            width: '100%',
            height: '100%',
            background: 'linear-gradient(135deg, #006064, #00838f)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#e0f7fa',
            fontSize: `${size * 0.2}px`,
            fontWeight: 'bold'
          }}>
            ?
          </div>
        )}
      </Link>
    </div>
  );
};

interface PostDetailNavbarProps {
  onBack: () => void;
  onLogout: () => void;
  currentUser: any;
  notifications: any[];
  unreadCount: number;
  activeTab: 'all' | 'following' | 'friends';
  onTabChange: (tab: 'all' | 'following' | 'friends') => void;
  theme: any;
  showNotifications: boolean;
  setShowNotifications: (show: boolean) => void;
  showProfileMenu: boolean;
  setShowProfileMenu: (show: boolean) => void;
  onNotificationClick?: (notification: any) => void;
  hideTabs?: boolean;
}

export default function PostDetailNavbar({
  onBack,
  onLogout,
  currentUser,
  notifications,
  unreadCount,
  activeTab,
  onTabChange,
  theme,
  showNotifications,
  setShowNotifications,
  showProfileMenu,
  setShowProfileMenu,
  onNotificationClick,
  hideTabs
}: PostDetailNavbarProps) {
  const scale = useScale();

  // Memoize event handlers
  const handleToggleNotifications = useCallback(() => {
    setShowNotifications(!showNotifications);
  }, [showNotifications, setShowNotifications]);

  const handleCloseNotifications = useCallback(() => {
    setShowNotifications(false);
  }, [setShowNotifications]);

  const handleToggleProfileMenu = useCallback(() => {
    setShowProfileMenu(!showProfileMenu);
  }, [showProfileMenu, setShowProfileMenu]);

  const handleCloseProfileMenu = useCallback(() => {
    setShowProfileMenu(false);
  }, [setShowProfileMenu]);

  const handleLogoutClick = useCallback(() => {
    setShowProfileMenu(false);
    onLogout();
  }, [setShowProfileMenu, onLogout]);

  const handleMarkAllRead = useCallback(async () => {
    try {
      await fetchWithAuth('/api/notifications/read-all', {
        method: 'POST',
        credentials: 'include'
      });
    } catch (error) {
    }
  }, []);

  const handleTabAll = useCallback(() => {
    onTabChange('all');
  }, [onTabChange]);

  const handleTabFollowing = useCallback(() => {
    onTabChange('following');
  }, [onTabChange]);

  const handleTabFriends = useCallback(() => {
    onTabChange('friends');
  }, [onTabChange]);

  return (
    <>
      {/* Navbar */}
      <nav className="sticky top-0 z-50 w-full border-b" style={{
        borderColor: theme.accentColor + '40'
      }}>
        <div className="w-full flex items-center justify-center" style={{
          paddingLeft: `${scaleW(32, scale.w)}px`,
          paddingRight: `${scaleW(32, scale.w)}px`,
          height: `${scaleH(80, scale.h)}px`
        }}>
          <div className="w-full flex items-center justify-between" style={{
            paddingLeft: `${scaleW(40, scale.w)}px`,
            paddingRight: `${scaleW(80, scale.w)}px`,
            gap: `${scaleW(32, scale.w)}px`
          }}>
            {/* Left Side - Back Button + EarthSocial Title */}
            <div className="flex items-center gap-4 flex-shrink-0" style={{ marginLeft: '-2.5rem' }}>
              <button
                onClick={onBack}
                className="p-2 rounded-lg hover:opacity-80 transition-opacity"
                style={{ color: theme.textColor }}
              >
                <ArrowLeft size={24} />
              </button>
              <h1 className="font-bold" style={{
                color: theme.textColor,
                fontSize: `${scaleW(48, scale.w)}px`
              }}>
                Earth<span style={{ color: '#E91E63' }}>Social</span>
              </h1>
            </div>

            {/* Spacer to push items to the right */}
            <div className="flex-1" />

            {/* Notifications Bell */}
            <div className="relative flex-shrink-0" style={{ marginTop: `${scaleH(1.6, scale.h)}px`, marginRight: `${scaleW(34, scale.w)}px` }}>
              <button
                onClick={handleToggleNotifications}
                className="relative p-2 transition-all hover:opacity-80"
                style={{ color: theme.textColor }}
              >
                <Bell size={Math.round(scaleW(32, scale.w))} />
                {unreadCount > 0 && (
                  <span
                    className="absolute -top-1 -right-1 rounded-full text-xs font-bold px-1.5 py-0.5"
                    style={{
                      backgroundColor: '#ef4444',
                      color: 'white',
                      fontSize: `${scaleW(12, scale.w)}px`,
                      minWidth: `${scaleW(20, scale.w)}px`,
                      textAlign: 'center'
                    }}
                  >
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </span>
                )}
              </button>

                {/* Notifications Dropdown */}
                {showNotifications && (
                  <>
                    <div
                      className="absolute top-full right-0 mt-2 rounded-lg overflow-hidden shadow-2xl backdrop-blur-sm"
                      style={{
                        background: theme.cardBg,
                        borderColor: theme.accentColor + '40',
                        border: '1px solid',
                        width: `${scaleW(400, scale.w)}px`,
                        maxHeight: `${scaleH(500, scale.h)}px`,
                        zIndex: 1001
                      }}
                    >
                      <div
                        className="flex items-center justify-center p-4 border-b relative"
                        style={{
                          borderColor: theme.accentColor + '40',
                          backgroundColor: theme.tabBarBg || theme.navbarBg
                        }}
                      >
                        <h3 className="font-bold text-lg" style={{ color: theme.textColor }}>
                          Notifications
                        </h3>
                        {unreadCount > 0 && (
                          <button
                            className="absolute right-4 text-sm hover:opacity-80 transition-opacity"
                            onClick={handleMarkAllRead}
                            style={{ color: theme.accentColor }}
                          >
                            Mark all read
                          </button>
                        )}
                      </div>

                      <div className="overflow-y-auto" style={{ maxHeight: `${scaleH(440, scale.h)}px` }}>
                        {notifications.length === 0 ? (
                          <div className="flex flex-col items-center justify-center py-12" style={{ color: theme.textColor + '99' }}>
                            <Bell size={48} style={{ color: theme.textColor + '40' }} className="mb-3" />
                            <p className="text-sm">No notifications yet</p>
                          </div>
                        ) : (
                          notifications.map((notification) => (
                            <div
                              key={notification.id}
                              onClick={() => onNotificationClick?.(notification)}
                              className="p-4 border-b cursor-pointer transition-all hover:opacity-80"
                              style={{
                                backgroundColor: notification.read ? 'transparent' : theme.accentColor + '10',
                                borderColor: theme.accentColor + '20'
                              }}
                            >
                              <p className="text-sm" style={{ color: theme.textColor }}>
                                {notification.content || 'New notification'}
                              </p>
                            </div>
                          ))
                        )}
                      </div>
                    </div>

                    <div
                      className="fixed inset-0"
                      style={{ zIndex: 1000 }}
                      onClick={handleCloseNotifications}
                    />
                  </>
                )}
              </div>

            {/* Right Side - Profile/Logo */}
            <div className="flex flex-col items-center flex-shrink-0" style={{
              overflow: 'visible',
              zIndex: 100,
              position: 'relative',
              height: `${scaleH(80, scale.h)}px`
            }}>
              {/* Turtle with clickable profile picture */}
              <div
                style={{
                  display: 'block',
                  lineHeight: 0,
                  padding: 0,
                  margin: 0,
                  width: `${scaleW(68, scale.w)}px`,
                  height: `${scaleH(87, scale.h)}px`,
                  overflow: 'visible',
                  position: 'absolute',
                  top: `${scaleH(-24, scale.h)}px`,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  zIndex: 100
                }}
              >
                <TurtleWithProfile
                  size={scaleW(68, scale.w)}
                  profilePicture={currentUser?.profilePicture || currentUser?.avatar}
                />
              </div>

              {/* Dropdown Toggle Button - Centered directly below turtle */}
              <button
                onClick={handleToggleProfileMenu}
                className="hover:opacity-80 transition-opacity"
                style={{
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '0px',
                  position: 'absolute',
                  top: `${scaleH(63, scale.h)}px`,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  zIndex: 101
                }}
              >
                <ChevronDown size={scaleH(14, scale.h)} style={{ color: theme.textColor }} />
              </button>

              {/* Profile Dropdown Menu */}
              {showProfileMenu && (
                <>
                  {/* Backdrop */}
                  <div
                    className="fixed inset-0"
                    style={{ zIndex: 999 }}
                    onClick={handleCloseProfileMenu}
                  />
                  {/* Menu */}
                  <div
                    className="absolute top-full right-0 mt-2 rounded-lg shadow-lg border"
                    style={{
                      background: theme.cardBg,
                      borderColor: theme.accentColor + '40',
                      minWidth: '200px',
                      zIndex: 1000
                    }}
                  >
                    <Link
                      to="/u"
                      className="flex items-center gap-3 px-4 py-3 hover:opacity-80 transition-opacity border-b"
                      style={{ color: theme.textColor, borderColor: theme.accentColor + '20' }}
                      onClick={handleCloseProfileMenu}
                    >
                      <Users size={20} />
                      <span>My Profile</span>
                    </Link>
                    <Link
                      to="/settings"
                      className="flex items-center gap-3 px-4 py-3 hover:opacity-80 transition-opacity border-b"
                      style={{ color: theme.textColor, borderColor: theme.accentColor + '20' }}
                      onClick={handleCloseProfileMenu}
                    >
                      <Settings size={20} />
                      <span>Settings</span>
                    </Link>
                    <button
                      onClick={handleLogoutClick}
                      className="w-full flex items-center gap-3 px-4 py-3 hover:opacity-80 transition-opacity"
                      style={{ color: theme.textColor }}
                    >
                      <LogOut size={20} />
                      <span>Sign Out</span>
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Tab Bar */}
      {!hideTabs && (
      <div className="sticky z-40 w-full border-b" style={{
        top: `${scaleH(80, scale.h)}px`,
        borderColor: theme.accentColor + '40'
      }}>
        <div className="flex justify-center gap-4 px-8 py-4">
          <button
            onClick={handleTabAll}
            className="text-lg font-semibold pb-1 transition-all duration-300 border-b-2"
            style={{
              color: activeTab === 'all' ? theme.textColor : theme.textColor + '99',
              borderColor: activeTab === 'all' ? theme.accentColor : 'transparent'
            }}
            onMouseEnter={(e) => {
              if (activeTab !== 'all') {
                e.currentTarget.style.color = theme.textColor;
              }
            }}
            onMouseLeave={(e) => {
              if (activeTab !== 'all') {
                e.currentTarget.style.color = theme.textColor + '99';
              }
            }}
          >
            All
          </button>
          <button
            onClick={handleTabFollowing}
            className="text-lg font-semibold pb-1 transition-all duration-300 border-b-2"
            style={{
              color: activeTab === 'following' ? theme.textColor : theme.textColor + '99',
              borderColor: activeTab === 'following' ? theme.accentColor : 'transparent'
            }}
            onMouseEnter={(e) => {
              if (activeTab !== 'following') {
                e.currentTarget.style.color = theme.textColor;
              }
            }}
            onMouseLeave={(e) => {
              if (activeTab !== 'following') {
                e.currentTarget.style.color = theme.textColor + '99';
              }
            }}
          >
            Following
          </button>
          <button
            onClick={handleTabFriends}
            className="text-lg font-semibold pb-1 transition-all duration-300 border-b-2"
            style={{
              color: activeTab === 'friends' ? theme.textColor : theme.textColor + '99',
              borderColor: activeTab === 'friends' ? theme.accentColor : 'transparent'
            }}
            onMouseEnter={(e) => {
              if (activeTab !== 'friends') {
                e.currentTarget.style.color = theme.textColor;
              }
            }}
            onMouseLeave={(e) => {
              if (activeTab !== 'friends') {
                e.currentTarget.style.color = theme.textColor + '99';
              }
            }}
          >
            Friends
          </button>
        </div>
      </div>
      )}
    </>
  );
}
